'use client';

import { LoadingSpinner } from '@govinity/ui/components/ui/loading-spinner';
import { LoadingError } from '@govinity/ui/components/ui-custom/loading-error';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notFound, useParams, usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { NavbarUserAvatar } from '@/app/navbar-user-avatar';
import { getMenuItemsFromCode, LOGIN_URL } from '@/app/constants';
import { Footer } from '@/components/ui/footer';
import { PortalHeroBanner } from '@/components/ui/portal-hero-banner';
import { env } from '@/env.mjs';
import api from '@/lib/api';
import { useCanPreviewHiddenPortalPages } from '@/lib/hooks/use-can-preview-hidden-portal-pages';
import { useAuthStore } from '@/stores/authStore';
import { useGovernmentStore } from '@/stores/governmentStore';
import { PortalGoogleAnalytics } from '@/components/portal-google-analytics';
import { HiddenPageBanner } from './components/hidden-page-banner';
import { GovernmentContext } from './context';
import { RealtimeProvider } from './realtime-provider';

type NavKey = 'commission' | 'documents' | 'home' | 'meetings' | 'boards' | 'topics';

/** Route path segment -> pageHeadings key (schema uses commission, meetings, topics, boards, documents) */
const ROUTE_TO_PAGE_HEADING_KEY: Record<
  string,
  'boards' | 'commission' | 'documents' | 'meetings' | 'topics'
> = {
  boards: 'boards',
  commission: 'commission',
  meetings: 'meetings',
  actions: 'documents',
  topics: 'topics',
};

export function GovernmentLayout({ children }: { children: React.ReactNode }) {
  const t = useTranslations('GovernmentLayout');
  const { code } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const setGovernment = useGovernmentStore((state) => state.setGovernment);
  const setLegislativeTypes = useGovernmentStore((state) => state.setLegislativeTypes);
  const setLegislativeStatuses = useGovernmentStore((state) => state.setLegislativeStatuses);
  const setMeetingBodies = useGovernmentStore((state) => state.setMeetingBodies);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    return () => {
      setGovernment(null);
      setLegislativeTypes([]);
      setLegislativeStatuses([]);
      setMeetingBodies([]);
    };
  }, [setGovernment, setLegislativeTypes, setLegislativeStatuses, setMeetingBodies]);

  const {
    data: government,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['government', code],
    queryFn: async () => {
      const result = await api.governments.get(code as string);

      setGovernment(result);

      return result;
    },
    // We don't want to cache this data, we want to refetch it when the user lands in /portal/:code
    // for example when going back and forth between /account and /portal/:code
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });

  const { isLoading: legislativeTypesLoading, error: legislativeTypesError } = useQuery({
    enabled: !!government,
    queryKey: ['legislative-types', code],
    queryFn: async () => {
      const result = await api.governments.legislativeTypes(code as string).getAll();

      setLegislativeTypes(result);

      return result;
    },
  });

  const { isLoading: legislativeStatusesLoading, error: legislativeStatusesError } = useQuery({
    enabled: !!government,
    queryKey: ['legislative-statuses', code],
    queryFn: async () => {
      const result = await api.governments.legislativeStatuses(code as string).getAll();

      setLegislativeStatuses(result);

      return result;
    },
  });

  const { isLoading: meetingBodiesLoading, error: meetingBodiesError } = useQuery({
    enabled: !!government,
    queryKey: ['meeting-bodies', code],
    queryFn: async () => {
      const result = await api.governments.meetingBodies(code as string).getAll();

      setMeetingBodies(result);

      return result;
    },
  });

  const canPreviewHiddenPages = useCanPreviewHiddenPortalPages(government?.id);

  // Get menu items for the hero banner
  const menuItems = useMemo(() => {
    if (!government) return [];

    const { navLabels, navHiddenElements } = government.customizations ?? {};

    return getMenuItemsFromCode(code as string)
      .filter((item) => !navHiddenElements?.[item.key as NavKey])
      .map((item) => {
        const label = navLabels?.[item.key as NavKey];
        return label ? { ...item, title: label } : item;
      });
  }, [government, code]);

  const pageKey = useMemo(() => {
    const subPath = pathname.replace(`/portal/${code}`, '') || '/';
    const segment = subPath.slice(1).split('/')[0];

    return segment ? ROUTE_TO_PAGE_HEADING_KEY[segment] : undefined;
  }, [code, pathname]);

  const currentNavKey: NavKey | undefined = pathname === `/portal/${code}` ? 'home' : pageKey;

  // Get page-specific title and subtitle based on current pathname
  const pageHeading = useMemo(() => {
    if (!government) return null;

    const { customizations } = government;

    if (pageKey && customizations?.pageHeadings) {
      const heading = (
        customizations.pageHeadings as Record<
          string,
          { title?: string | null; subtitle?: string | null } | null
        >
      )[pageKey];
      if (heading) {
        return { title: heading.title ?? null, subtitle: heading.subtitle ?? null };
      }
    }

    return { title: customizations?.headerText, subtitle: customizations?.subheaderText };
  }, [government, pageKey]);

  if (isLoading || legislativeTypesLoading || legislativeStatusesLoading || meetingBodiesLoading) {
    return (
      <div className="flex h-screen items-center justify-center" id="main-content">
        <LoadingSpinner size={60} />
      </div>
    );
  }

  if (error && 'status' in error && error.status === 404) {
    return (
      <div id="main-content">
        <LoadingError
          className="mx-4 my-10 max-w-lg md:mx-auto"
          message={t('governmentNotFound')}
        />
      </div>
    );
  }

  if (
    !government ||
    error ||
    legislativeTypesError ||
    legislativeStatusesError ||
    meetingBodiesError
  ) {
    return (
      <div className="flex h-screen items-center justify-center" id="main-content">
        <LoadingError
          message={
            error?.message ||
            legislativeTypesError?.message ||
            legislativeStatusesError?.message ||
            meetingBodiesError?.message ||
            'Invalid Government'
          }
        />
      </div>
    );
  }

  // Agenda items link to topic details, so those stay public even when the Topics tab is hidden
  const isTopicDetailsPage = pathname.startsWith(`/portal/${code}/topics/`);
  const isCurrentPageHidden =
    !!currentNavKey &&
    !!government.customizations?.navHiddenElements?.[currentNavKey] &&
    !isTopicDetailsPage;

  // A hidden home page redirects to the first visible page instead (see page.tsx)
  if (isCurrentPageHidden && currentNavKey !== 'home' && !canPreviewHiddenPages) {
    notFound();
  }

  return (
    // overflow-x-clip (not overflow-x-hidden) so descendant `position: sticky`
    // elements stay relative to the viewport. overflow-x-hidden forces overflow-y
    // to compute as `auto`, which creates a new scrolling context that scopes
    // sticky positioning to this container instead of the page.
    <div className="dark:bg-background flex min-h-screen flex-col overflow-x-clip bg-slate-50">
      {government.gaMeasurementId && (
        <PortalGoogleAnalytics gaMeasurementId={government.gaMeasurementId} />
      )}
      <PortalHeroBanner
        menu={menuItems}
        auth={{
          logout: {
            text: t('logoutText'),
            url: `${env.NEXT_PUBLIC_USER_API_URL}/auth/logout?skip-login-redirect=true`,
          },
          showUserNav: !!user,
          ...(!user && {
            login: { text: t('loginText'), url: LOGIN_URL },
            signup: { text: t('signupText'), url: LOGIN_URL },
          }),
          settingsUrl: '/account/settings',
          showAccount: !!user,
          governmentPortalUrl:
            user && 'governmentId' in user ? env.NEXT_PUBLIC_INTERNAL_CLIENT_URL : undefined,
        }}
        userAvatarSlot={user ? <NavbarUserAvatar user={user} /> : null}
        showThemeToggle={env.NEXT_PUBLIC_APP_ENV === 'development'}
        title={pageHeading?.title}
        subtitle={pageHeading?.subtitle}
        government={government}
      />
      <main id="main-content" className="flex-1 px-1 pb-8 sm:px-2 md:px-8 lg:px-24 xl:px-32">
        <GovernmentContext.Provider
          value={{
            government,
            invalidate: () => {
              queryClient.invalidateQueries({ queryKey: ['government', code] });
            },
            path: `/portal/${code}`,
          }}
        >
          <RealtimeProvider governmentCode={government.code} governmentId={government.id}>
            {isCurrentPageHidden && canPreviewHiddenPages && <HiddenPageBanner />}
            {children}
          </RealtimeProvider>
        </GovernmentContext.Provider>
      </main>
      <Footer
        footerData={government.customizations?.footer}
        governmentName={government.name}
        logoClickUrl={government.customizations?.logoClickUrl}
      />
    </div>
  );
}
